import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchRedDomains, saveRedDomains } from './api/redDomainsApi'

const DEFAULT_ROW_COUNT = 10

function buildRows(saved) {
  const filled = saved.map((item) => ({
    id: item.id,
    value: String(item.domain || '').trim(),
    saved: true,
  }))
  const empty = Math.max(0, DEFAULT_ROW_COUNT - filled.length)
  const empties = Array.from({ length: empty }, (_, i) => ({
    id: `new-${Date.now()}-${i}`,
    value: '',
    saved: false,
  }))
  return [...filled, ...empties]
}

export default function RedDomainsScreen() {
  const [rows, setRows] = useState(() =>
    Array.from({ length: DEFAULT_ROW_COUNT }, (_, i) => ({ id: `empty-${i}`, value: '', saved: false })),
  )
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [error, setError] = useState(null)
  const successTimer = useRef(null)
  const privilegeSet = useRef(new Set())
  const isAdminRef = useRef(false)

  if (privilegeSet.current.size === 0) {
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}')
      const granted = Array.isArray(storedUser?.privileges) ? storedUser.privileges : []
      privilegeSet.current = new Set(granted.map((item) => String(item || '')))
      isAdminRef.current = Boolean(storedUser?.is_admin ?? storedUser?.isAdmin)
    } catch {
      privilegeSet.current = new Set()
      isAdminRef.current = false
    }
  }

  const canEdit = isAdminRef.current || privilegeSet.current.has('ProductivityRedDomainsEdit')
  const canAdd = isAdminRef.current || privilegeSet.current.has('ProductivityRedDomainsAdd')
  const canDelete = isAdminRef.current || privilegeSet.current.has('ProductivityRedDomainsDelete')
  const isReadOnly = !canEdit

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setError(null)
    fetchRedDomains()
      .then((domains) => {
        if (!cancelled) setRows(buildRows(domains))
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.detail || 'Failed to load red domains.')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleChange = useCallback(
    (id, value) => {
      if (isReadOnly) return
      setRows((prev) => prev.map((row) => (row.id === id ? { ...row, value } : row)))
      setSaveSuccess(false)
    },
    [isReadOnly],
  )

  const handleRemove = useCallback(
    (id) => {
      if (isReadOnly || !canDelete) return
      setRows((prev) => {
        const updated = prev.filter((row) => row.id !== id)
        return updated.length > 0 ? updated : [{ id: `empty-${Date.now()}`, value: '', saved: false }]
      })
      setSaveSuccess(false)
    },
    [canDelete, isReadOnly],
  )

  const handleAddRow = useCallback(() => {
    if (isReadOnly || !canAdd) return
    setRows((prev) => [...prev, { id: `new-${Date.now()}`, value: '', saved: false }])
    setSaveSuccess(false)
  }, [canAdd, isReadOnly])

  const handleSave = useCallback(async () => {
    if (isReadOnly || !canEdit) return
    const domains = rows.map((r) => r.value.replace(/^www\./, '').toLowerCase().trim()).filter(Boolean)
    setIsSaving(true)
    setError(null)
    setSaveSuccess(false)
    try {
      await saveRedDomains(domains)
      setSaveSuccess(true)
      const fresh = await fetchRedDomains()
      setRows(buildRows(fresh))
      clearTimeout(successTimer.current)
      successTimer.current = setTimeout(() => setSaveSuccess(false), 4000)
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save red domains.')
    } finally {
      setIsSaving(false)
    }
  }, [canEdit, isReadOnly, rows])

  const filledCount = rows.filter((r) => r.value.trim()).length

  return (
    <div className="mx-auto w-full max-w-2xl pb-16">
      <header className="mb-8">
        <div className="mb-2 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-600" aria-hidden="true">
            <span className="material-symbols-outlined text-xl">block</span>
          </span>
          <h1 className="text-3xl font-extrabold tracking-tight text-on-surface">Red Domains</h1>
        </div>
        <p className="text-sm text-on-surface-variant">
          Define domains that are flagged as non-productive. The agent detects and reports usage for these domains.
        </p>
        {isReadOnly ? (
          <p className="mt-2 text-xs font-semibold text-amber-700">Read-only mode: you do not have write privileges for Red Domains.</p>
        ) : null}
      </header>

      <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest shadow-sm">
        <div className="flex items-center justify-between border-b border-outline-variant/10 px-6 py-4">
          <div>
            <p className="text-sm font-semibold text-on-surface">Domain List</p>
            <p className="text-xs text-on-surface-variant">
              {filledCount} domain{filledCount !== 1 ? 's' : ''} configured
            </p>
          </div>
          {saveSuccess && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              <span className="material-symbols-outlined text-sm">check_circle</span>
              Saved
            </span>
          )}
        </div>

        <div className="px-6 py-4">
          {isLoading ? (
            <div className="flex h-48 items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : (
            <div className="space-y-2.5" role="list" aria-label="Red domain list">
              {rows.map((row, index) => (
                <div key={row.id} className="group flex items-center gap-3" role="listitem">
                  <span className="w-7 shrink-0 text-right text-xs font-bold text-on-surface-variant/50 select-none">{index + 1}</span>

                  <div className="relative flex-1">
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                      <span className="material-symbols-outlined text-sm text-on-surface-variant/50">language</span>
                    </span>
                    <input
                      id={`red-domain-input-${index}`}
                      type="text"
                      value={row.value}
                      onChange={(e) => handleChange(row.id, e.target.value)}
                      placeholder="youtube.com"
                      autoComplete="off"
                      readOnly={isReadOnly}
                      className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-low py-2.5 pl-9 pr-3 text-sm text-on-surface placeholder-on-surface-variant/40 transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      aria-label={`Red domain ${index + 1}`}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemove(row.id)}
                    title="Remove domain"
                    aria-label={`Remove domain ${row.value || index + 1}`}
                    disabled={isReadOnly || !canDelete}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-on-surface-variant/40 transition-colors hover:bg-red-50 hover:text-red-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <span className="material-symbols-outlined text-base">delete</span>
                  </button>
                </div>
              ))}
            </div>
          )}

          {!isLoading && (
            <button
              type="button"
              id="add-red-domain-btn"
              onClick={handleAddRow}
              disabled={isReadOnly || !canAdd}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-outline-variant/40 py-3 text-sm font-semibold text-on-surface-variant/70 transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span className="material-symbols-outlined text-base">add</span>
              Add Domain
            </button>
          )}
        </div>

        {error && (
          <div className="mx-6 mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between border-t border-outline-variant/10 px-6 py-4">
          <p className="text-xs text-on-surface-variant">
            Enter domains without <code className="rounded bg-surface-container px-1 py-0.5">www.</code> - e.g.{' '}
            <code className="rounded bg-surface-container px-1 py-0.5">youtube.com</code>
          </p>
          <button
            type="button"
            id="save-red-domains-btn"
            onClick={handleSave}
            disabled={isSaving || isLoading || isReadOnly || !canEdit}
            className="flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-on-primary shadow-sm transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {isSaving ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-on-primary border-t-transparent" />
                Saving...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-sm">save</span>
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-blue-100 bg-blue-50 px-5 py-4">
        <div className="flex gap-3">
          <span className="material-symbols-outlined mt-0.5 text-base text-blue-500">info</span>
          <div>
            <p className="text-sm font-semibold text-blue-800">How Red Domains work</p>
            <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-blue-700">
              <li>
                <strong>Active tracking:</strong> foreground red-domain browsing is logged as non-productive.
              </li>
              <li>
                <strong>Ambient detection:</strong> visible red domains in background windows are also flagged.
              </li>
              <li>Changes take effect on next agent sync cycle.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

